import { trim } from 'lodash';
import { createLogger } from 'shared/services/logger';
import us from '../../defines';
import {translate} from 'shared/services/I18nService';

const logger = createLogger('UScope:lib:xinfo');

// -------------------------------------------------------------------------------------------------
// xinfo.js
//    This file contains the request for xinfo data and the class, functions to access that data.
//    - http://localhost:82/@1?XINFO
//
//    history
//    1105    cperz        created
//    _______________________________________end_AIHv1/PR __________________________________________
//    141118    cperz
//    141222    cperz        change construction signature; use passed in log function; add support for analysis;
//    150318    cperz        decoded Algorithm for markups
//
// ________________________________________________________________________________________________
//

export default function XInfo () {
  const $t = translate.scope('uScope');
  let status = false; // initialized ok

  this.scanner = $t('not-defined');
  this.version = $t('not-defined');
  this.bitsPerSampl = 8;
  this.acqBitsPerSampl = 8;// todo: not parsed yet
  this.samplPerPix = 3;
  this.capturetype = us.eAICCaptureType.aic_i_BF;
  this.left = 0; // cp-i don't see it in xinfo but a tma core (sub image) should have a non-zero left,top coord - or should it when connect to imageserver?
  this.top = 0;
  this.width = 0;
  this.height = 0;
  this.depth = 0; //
  this.tileWidth = 0;
  this.tileHeight = 0;
  this.title = '';
  this.description = '';
  this.filesize = 0;
  this.imagesize = 0;
  this.comptype = 0;
  this.comptypedesc = '';
  this.compquality = 0;
  this.compcodec = 0;
  this.apparentMag = 20;
  this.MPP = 0.5;
  this.sessionMode = '';

  this.isFused = false;
  this.fusedFiles = []; // fused member image file names

  this.layers = 0; // layer object array that contains level object arrays
  this.layerTbl = [];

  this.thumb = new Thumbnail();
  this.macro = new Macro();
  this.label = new Label();

  // public methods
  // ............................................ status related
  this.getStatus = function () {
    return status;
  };
  this.setStatus = function (bstate) {
    status = (bstate === true);
  };

  // ............................................ image pyramid access
  // defaults lev=0,lay=0
  this.GetLevelObj = function (lev = 0, lay = 0) { // get level object for pyramid(layer, level
    if (lay > this.layers || lev > this.layerTbl[lay].levels) {
      return null;
    }
    return this.layerTbl[lay].levelTbl[lev];
  };
  this.get_imageLevels = function (lay = 0) { // get number of image pyramid levels
    return +this.layerTbl[lay].levels;
  };
  this.get_imageZoom = function (lev, lay) { // get image pyramid level zoom  (e.g. 1, 4, 16)
    var oLevel = this.GetLevelObj(lev, lay);
    return oLevel != null ? +oLevel.zoom : 0;
  };
  this.get_imageWidth = function (lev, lay) { // get image width for a given layer,level
    var oLevel = this.GetLevelObj(lev, lay);
    return oLevel != null ? +oLevel.width : 0;
  };
  this.get_imageHeight = function (lev, lay) { // get image height for a given layer,level
    var oLevel = this.GetLevelObj(lev, lay);
    return oLevel != null ? +oLevel.height : 0;
  };
  this.get_tilesX = function (lev, lay) { // get number of tiles in X for a given layer,level
    var oLevel = this.GetLevelObj(lev, lay);
    return oLevel != null ? +oLevel.tilesX : 0;
  };
  this.get_tilesY = function (lev, lay) { // get number of tiles in Y for a given layer,level
    var oLevel = this.GetLevelObj(lev, lay);
    return oLevel != null ? +oLevel.tilesY : 0;
  };
  /**
   * Gets the apparent magnification value from the image description. This corresponds to the magnification the
   * image was scanned at.
   * @returns {number}
   */
  this.get_baseMag = function () {
    return this.apparentMag;
  };
  /**
   * Returns microns-per-pixel for the base layer/level
   * @returns {number}
   */
  this.get_MPP = function () {
    var zoom = this.get_imageZoom();
    var MPP = this.MPP * zoom;
    return MPP;
  };
  this.getAlgorithmName = function () {
    return this.algorithmName;
  };

  // ............................................ parse xinfo response, populate XINFO object - call back from ajaxXINFO
  // improvement: convert to use of x2js
  this.init = function (xinfoDoc) {
    // verify xinfo is the response
    if (!jQuery.isXMLDoc(xinfoDoc) || xinfoDoc.getElementsByTagName('xinfo')[0].nodeName !== 'xinfo') {
      logger.error('Error:  uScope: xinfo: init: input xinfoDoc is not xinfo content');
      return status;
    }

    this.version = xinfoDoc.getElementsByTagName('version')[0].childNodes[0].nodeValue;
    this.bitsPerSampl = Number(xinfoDoc.getElementsByTagName('bitsPerSampl')[0].childNodes[0].nodeValue);
    this.samplPerPix = Number(xinfoDoc.getElementsByTagName('samplPerPix')[0].childNodes[0].nodeValue);
    this.capturetype = Number(xinfoDoc.getElementsByTagName('capturetype')[0].childNodes[0].nodeValue);
    this.width = Number(xinfoDoc.getElementsByTagName('width')[0].childNodes[0].nodeValue);
    this.height = Number(xinfoDoc.getElementsByTagName('height')[0].childNodes[0].nodeValue);
    this.depth = Number(xinfoDoc.getElementsByTagName('depth')[0].childNodes[0].nodeValue);
    this.tileWidth = Number(xinfoDoc.getElementsByTagName('tilewidth')[0].childNodes[0].nodeValue);
    this.tileHeight = Number(xinfoDoc.getElementsByTagName('tileheight')[0].childNodes[0].nodeValue);

    if (xinfoDoc.getElementsByTagName('title').firstChild != null) { // title not always present
      this.title = xinfoDoc.getElementsByTagName('title')[0].childNodes[0].nodeValue;
    }

    // get the image description and parse it. Here's a typical description string:
    //        "Aperio Image Library v10.0.51 42320x31454 [0,100 41392x31354] (256x256) JPEG/RGB Q=70|
    //        AppMag = 20|StripeWidth = 1840|ScanScope ID = SS5340|Filename = 347031|Date = 11/10/09|
    //        Time = 12:09:18|User = d74add0a-174c-468a-bf95-d7b4d274a196|MPP = 0.5015|Left = 35.625134|
    //        Top = 21.241474|LineCameraSkew = -0.000185|LineAreaXOffset = 0.032367|LineAreaYOffset = -0.065287|
    //        Focus Offset = -0.001000|DSR ID = dsr-mfg|ImageID = 347031|OriginalWidth = 42320|Originalheight = 31454|
    //        ICC Profile = ScanScope v1"

    var i, len;

    // ............................................ search description field related (if present)
    if (xinfoDoc.getElementsByTagName('description')[0].childNodes[0]) {
      this.description = xinfoDoc.getElementsByTagName('description')[0].childNodes[0].nodeValue;
      const descArray = this.description.split('|'); // | delimits the aperio description string

      for (i = 1, len = descArray.length; i < len; i++) { // starting after the "Aperio Image Library" item
        if (descArray[i].indexOf('=') != -1) { // if there is a name = value item
          var newObj = descArray[i].split('='); // then add it to this xinfo object
          this[jQuery.trim(newObj[0])] = jQuery.trim(newObj[1]);
        }
      }

      const getDescValue = (tag, defaultValue = null) => {
        if (!descArray) {
          return defaultValue;
        }
        for (let i = 0, len = descArray.length; i < len; i++) {
          if (descArray[i].includes(tag)) {
            const valIdx = descArray[i].indexOf('=') + 1;
            let value = descArray[i].slice(valIdx);
            value = trim(value);
            return value;
          }
        }
        return defaultValue;
      };

      const getNumericDescValue = (tag, defaultValue = 0) => {
        const val = getDescValue(tag, defaultValue);
        const out = parseFloat(val);
        return isNaN(out) ? defaultValue : out;
      };

      this.apparentMag = getNumericDescValue('AppMag', 20);
      this.scanner = (this.description.split(' ')[0]).split(';')[0]; // Ariol;ASE requires the split(';')
      this.MPP = getNumericDescValue('MPP', 0.5);
      this.filename = getDescValue('Filename');
      this.date = getDescValue('Date');
      this.scanScopeId = getDescValue('ScanScope ID');
      this.iccProfile = getDescValue('ICC Profile');
      this.acqBitsPerSampl = getNumericDescValue('Acquisition Bit Depth');
      this.algorithmName = getDescValue('Algorithm');
      this.sessionMode = getDescValue('SessionMode') || getDescValue('SessonMode'); // Some images have misspelt "Session"

      let memberFile;
      for (i = 0; i < 12; i++) {
        memberFile = getDescValue('Fused Image ' + i);
        if (memberFile) {
          this.fusedFiles[i] = memberFile;
        } else {
          break;
        }
      }
    }

    this.isFused = (this.fusedFiles.length > 0);
    this.filesize = +xinfoDoc.getElementsByTagName('filesize')[0].childNodes[0].nodeValue;
    this.imagesize = +xinfoDoc.getElementsByTagName('imagesize')[0].childNodes[0].nodeValue;
    this.comptype = +xinfoDoc.getElementsByTagName('comptype')[0].childNodes[0].nodeValue;
    this.comptypedesc = xinfoDoc.getElementsByTagName('comptypedesc')[0].childNodes[0].nodeValue;
    this.compquality = +xinfoDoc.getElementsByTagName('compquality')[0].childNodes[0].nodeValue;
    this.compcodec = xinfoDoc.getElementsByTagName('compcodec')[0].childNodes[0].nodeValue;

    // get the thumbnail before the levels (seen old imageservers report AFI image levels with widths of the thumbnail and of 0)
    var subImage = xinfoDoc.getElementsByTagName('thumb'); // thumb not always present
    if (subImage.length) {
      this.thumb.init(subImage[0].getElementsByTagName('width')[0].childNodes[0].nodeValue,
        subImage[0].getElementsByTagName('height')[0].childNodes[0].nodeValue,
        subImage[0].getElementsByTagName('zoom')[0].childNodes[0].nodeValue);
    }

    subImage = xinfoDoc.getElementsByTagName('macro'); // macro not always present
    if (subImage.length) {
      this.macro.init(subImage[0].getElementsByTagName('width')[0].childNodes[0].nodeValue,
        subImage[0].getElementsByTagName('height')[0].childNodes[0].nodeValue);
    }

    subImage = xinfoDoc.getElementsByTagName('label'); // label not always present
    if (subImage.length) {
      this.label.init(subImage[0].getElementsByTagName('width')[0].childNodes[0].nodeValue,
        subImage[0].getElementsByTagName('height')[0].childNodes[0].nodeValue);
    }

    this.layers = xinfoDoc.getElementsByTagName('layers')[0].childNodes[0].nodeValue;
    var xLayers = xinfoDoc.getElementsByTagName('layer');
    for (var lay = 0, maxLay = this.layers; lay < maxLay; lay++) { // for each layer
      this.layerTbl[lay] = new Layer(lay,
        xLayers[lay].getElementsByTagName('zoff')[0].childNodes[0].nodeValue,
        xLayers[lay].getElementsByTagName('depth')[0].childNodes[0].nodeValue,
        xLayers[lay].getElementsByTagName('focus')[0].childNodes[0].nodeValue,
        xLayers[lay].getElementsByTagName('levels')[0].childNodes[0].nodeValue
      );
      var nlevels = 0;
      var xLevels = xLayers[lay].getElementsByTagName('level');

      for (var lev = 0, maxLev = this.layerTbl[lay].levels; lev < maxLev; lev++) { // for each level
        // don't add a level that is smaller than the thumbnail unless it is the base level
        // note - sometimes XINFO of fused imagery reports this.  It may be fixed.
        var levWidth = +(xLevels[lev].getElementsByTagName('width')[0].childNodes[0].nodeValue);
        if (lev !== 0 && levWidth < this.thumb.width) {
          logger.warn('.... xinfo:init: ignoring level ' + lev + 'width=' + levWidth);
          continue;
        }
        this.layerTbl[lay].levelTbl[lev] = new Level(lev,
          xLevels[lev].getElementsByTagName('width')[0].childNodes[0].nodeValue,
          xLevels[lev].getElementsByTagName('height')[0].childNodes[0].nodeValue,
          xLevels[lev].getElementsByTagName('zoom')[0].childNodes[0].nodeValue,
          this.tileWidth,
          this.tileHeight);
        nlevels++;
      }

      this.layerTbl[lay].levels = nlevels;
    }
    status = true;
    return status;
  };

  // ............................................ xinfo thumb object
  function Thumbnail () { // object for thumbnail
    this.isValid = false;
    this.width = 0;
    this.height = 0;
    this.zoom = 1.0;

    this.init = function (w, h, zm) {
      this.width = +w;
      this.height = +h;
      this.zoom = +zm;
      this.isValid = true; // cp-more stringent test here
    };
  }

  // ............................................ xinfo macro image object
  function Macro () {
    this.isValid = false;
    this.width = 0;
    this.height = 0;

    this.init = function (w, h) {
      this.width = +w;
      this.height = +h;
      this.isValid = true; // cp-more stringent test here
    };
  }

  // ............................................ xinfo label object
  function Label () {
    this.isValid = false;
    this.width = 0;
    this.height = 0;

    this.init = function (w, h) {
      this.width = +w;
      this.height = +h;
      this.isValid = true; // cp-more stringent test here
    };
  }

  // ............................................ xinfo level object - levels of the image pyramid
  function Level (id, w, h, zm, tileW, tileH) {
    this.id = Number(id);
    this.width = Number(w);
    this.height = Number(h);
    this.zoom = Number(zm);

    this.tilesX = Math.round(this.width / tileW); // (this.width + tileW - 1) / tileW;
    this.tilesY = Math.round(this.height / tileH);
  }

  // ............................................ xinfo layer object - (z position of the microscope objective)
  function Layer (id, zoff, depth, focus, levels) {
    this.id = id;
    this.zoff = zoff;
    this.depth = depth;
    this.focus = focus;
    this.levels = levels;
    this.levelTbl = [];
  }
}

const cache = {};

XInfo.load = function (ajax, imageId) {
  return new Promise((resolve, reject) => {
    if (cache[imageId]) {
      const xinfo = new XInfo();
      xinfo.init(cache[imageId]);
      resolve(xinfo);
    } else {
      const onSuccess = (data) => {
        cache[imageId] = data;
        const xinfo = new XInfo();
        xinfo.init(data);
        resolve(xinfo);
      };

      const onError = (jqXHR, textStatus, errorThrown) => {
        logger.error('XINFO: ' + ajax.getErrorStr(jqXHR, textStatus, errorThrown, imageId));
        const error = new Error(textStatus);
        error.src = jqXHR;
        reject(error);
      };

      ajax.doIMGSRVREQ(onSuccess, onError, 'XINFO', {'request': 'XINFO', 'imageId': imageId}, 'xml');
    }
  });
};



// WEBPACK FOOTER //
// ./src/uScope/js/lib/data/xinfo.js